import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  IAddHolidayRequest,
  IUpdateHolidayRequest,
  ICreateHolidayTypeRequest,
  IUpdateHolidayTypeRequest,
  TAddHolidayResponse,
  TDeleteHolidayResponse,
  TGetHolidayByIdRequest,
  TGetHolidayByIdResponse,
  TGetHolidaysListingRequest,
  TGetHolidaysListingResponse,
  TGetHolidaysPaginatedRequest,
  TGetHolidaysPaginatedResponse,
  TGetHolidayTypesResponse,
  TUpdateHolidayResponse,
  TCreateHolidayTypeResponse,
  TUpdateHolidayTypeResponse,
  TDeleteHolidayTypeResponse,
} from '@/types/(company)/holidays.interface';
import { dynamicBaseQuery } from '@/api/DynamicBaseQuery';

const BASE = '/company/settings/holidays';
const TYPES_BASE = '/company/settings/holiday-types';

export const companyHolidaysApi = createApi({
  reducerPath: 'companyHolidaysApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['Holidays', 'HolidayTypes'],
  endpoints: (builder) => ({
    // Holiday Types
    getHolidayTypes: builder.query<TGetHolidayTypesResponse, void>({
      query: () => ({ url: TYPES_BASE, method: 'GET' }),
      providesTags: ['HolidayTypes'],
    }),

    createHolidayType: builder.mutation<TCreateHolidayTypeResponse, ICreateHolidayTypeRequest>({
      query: (data) => ({ url: TYPES_BASE, method: 'POST', body: data }),
      invalidatesTags: ['HolidayTypes'],
    }),

    updateHolidayType: builder.mutation<TUpdateHolidayTypeResponse, IUpdateHolidayTypeRequest>({
      query: ({ id, data }) => ({ url: `${TYPES_BASE}/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: ['HolidayTypes'],
    }),

    deleteHolidayType: builder.mutation<TDeleteHolidayTypeResponse, number>({
      query: (id) => ({ url: `${TYPES_BASE}/${id}`, method: 'DELETE' }),
      invalidatesTags: ['HolidayTypes'],
    }),

    // Holidays
    getAllHolidays: builder.query<TGetHolidaysListingResponse, TGetHolidaysListingRequest>({
      query: () => ({ url: BASE, method: 'GET' }),
      providesTags: ['Holidays'],
    }),

    getPaginatedHolidays: builder.query<TGetHolidaysPaginatedResponse, TGetHolidaysPaginatedRequest>({
      query: (params) => ({ url: `${BASE}/paginated`, method: 'GET', params }),
      providesTags: ['Holidays'],
    }),

    getHolidayById: builder.query<TGetHolidayByIdResponse, TGetHolidayByIdRequest>({
      query: ({ id }) => ({ url: `${BASE}/${id}`, method: 'GET' }),
      providesTags: (_, __, { id }) => [{ type: 'Holidays', id }],
    }),

    addHoliday: builder.mutation<TAddHolidayResponse, IAddHolidayRequest>({
      query: (data) => ({ url: BASE, method: 'POST', body: data }),
      invalidatesTags: ['Holidays'],
    }),

    updateHoliday: builder.mutation<TUpdateHolidayResponse, IUpdateHolidayRequest>({
      query: ({ id, data }) => ({ url: `${BASE}/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: ['Holidays'],
    }),

    deleteHoliday: builder.mutation<TDeleteHolidayResponse, number>({
      query: (id) => ({ url: `${BASE}/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Holidays'],
    }),
  }),
});

export const {
  useGetHolidayTypesQuery,
  useCreateHolidayTypeMutation,
  useUpdateHolidayTypeMutation,
  useDeleteHolidayTypeMutation,
  useGetAllHolidaysQuery,
  useGetPaginatedHolidaysQuery,
  useGetHolidayByIdQuery,
  useAddHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
} = companyHolidaysApi;
